import React from 'react';
import { FONTS } from '../../styles/tokens';

interface DeploySuccessCardProps {
  name: string;
  appUrl: string;
  version: number;
  isDark: boolean;
  colors: { text: string; textSecondary: string; border: string; card: string };
}

/**
 * Celebratory success card shown on the deploy form after the pipeline
 * completes. Replaces the old plain-text "Your app is live!" message.
 */
export function DeploySuccessCard(props: DeploySuccessCardProps): React.JSX.Element {
  return (
    <div
      style={{
        marginTop: '20px',
        padding: '24px',
        borderRadius: '14px',
        border: '1px solid rgba(74,222,128,0.35)',
        background: props.isDark
          ? 'linear-gradient(135deg, rgba(74,222,128,0.08), rgba(1,169,237,0.06))'
          : 'linear-gradient(135deg, rgba(74,222,128,0.12), rgba(1,169,237,0.08))',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <SuccessBadge />
      <h2
        style={{
          fontFamily: FONTS.heading,
          fontSize: '22px',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color: props.colors.text,
          margin: '8px 0 4px',
        }}
      >
        {props.name} is live
      </h2>
      <p style={{ fontFamily: FONTS.body, fontSize: '13px', color: props.colors.textSecondary, margin: '0 0 16px' }}>
        Build v{props.version} deployed and healthy.
      </p>

      <MetadataChips />

      <LaunchLink href={props.appUrl} isDark={props.isDark} />

      <UrlDisplay url={props.appUrl} colors={props.colors} />
    </div>
  );
}

/** Rounded green badge with a checkmark. */
function SuccessBadge(): React.JSX.Element {
  return (
    <div
      style={{
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: 'rgba(74,222,128,0.18)',
        border: '1px solid rgba(74,222,128,0.45)',
        display: 'grid',
        placeItems: 'center',
        color: '#4ADE80',
        fontFamily: FONTS.heading,
        fontSize: '18px',
      }}
    >
      ✓
    </div>
  );
}

/** Metadata chips — Status + SSO. Replaces the plain "Status: Running" lines. */
function MetadataChips(): React.JSX.Element {
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
      <Chip label="Running" accent="#4ADE80" />
      <Chip label="SSO via Keycloak" accent="#01A9ED" />
      <Chip label="Healthy" accent="#FBBF24" />
    </div>
  );
}

/** Small status chip. */
function Chip({ label, accent }: {
  label: string;
  accent: string;
}): React.JSX.Element {
  return (
    <span
      style={{
        fontFamily: FONTS.heading,
        fontSize: '9px',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        padding: '4px 8px',
        borderRadius: '999px',
        color: accent,
        border: `1px solid ${accent}66`,
        backgroundColor: `${accent}18`,
      }}
    >
      {label}
    </span>
  );
}

/** Large gradient button that opens the deployed app in a new tab. */
function LaunchLink({ href, isDark }: { href: string; isDark: boolean }): React.JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-block',
        padding: '12px 22px',
        borderRadius: '10px',
        background: isDark
          ? 'linear-gradient(90deg, #0266C6, #00407D)'
          : 'linear-gradient(90deg, #01A9ED, #0165C6)',
        color: '#FFF',
        fontFamily: FONTS.heading,
        fontSize: '12px',
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        textDecoration: 'none',
        boxShadow: '0 8px 24px rgba(1,169,237,0.28)',
      }}
    >
      Launch app →
    </a>
  );
}

/** Plain-text URL display + copy-friendly format. */
function UrlDisplay({ url, colors }: { url: string; colors: { textSecondary: string; border: string } }): React.JSX.Element {
  return (
    <div style={{ marginTop: '14px', fontFamily: 'monospace', fontSize: '11px', color: colors.textSecondary, wordBreak: 'break-all' }}>
      {url}
    </div>
  );
}
